import type { ComponentPropsWithoutRef } from "react";

import completeIcon from "@/assets/figma/chat-label/complete.svg";
import dotIcon from "@/assets/figma/chat-label/dot.svg";
import progressIcon from "@/assets/figma/chat-label/progress.svg";
import questionIcon from "@/assets/figma/chat-label/question.svg";
import { cn } from "@/shared/lib/cn";
import {
  getMerryChatLabelFigmaNodeId,
  getMerryChatLabelTextFigmaNodeId,
  type MerryChatLabelSize,
  type MerryChatLabelType,
} from "@/shared/ui/merryChatLabelVariants";

type NativeSpanProps = Omit<
  ComponentPropsWithoutRef<"span">,
  "aria-hidden" | "aria-label" | "children"
>;

interface MerryChatLabelBaseProps extends NativeSpanProps {
  accessibleLabel?: string;
  size?: MerryChatLabelSize;
}

interface MerryChatCountLabelProps extends MerryChatLabelBaseProps {
  count: number | string;
  type: "count";
}

interface MerryChatStatusLabelProps extends MerryChatLabelBaseProps {
  count?: never;
  type: Exclude<MerryChatLabelType, "count">;
}

export type MerryChatLabelProps =
  | MerryChatCountLabelProps
  | MerryChatStatusLabelProps;

const statusIcons: Record<
  Exclude<MerryChatLabelType, "count">,
  string
> = {
  complete: completeIcon,
  dot: dotIcon,
  progress: progressIcon,
  question: questionIcon,
};

export function MerryChatLabel({
  accessibleLabel,
  className,
  size = 16,
  ...props
}: MerryChatLabelProps) {
  const getCommonProps = (type: MerryChatLabelType) => ({
    "aria-hidden": accessibleLabel ? undefined : (true as const),
    "aria-label": accessibleLabel,
    className: cn("merry-chat-label", className),
    "data-chat-label-size": size,
    "data-chat-label-type": type,
    "data-figma-node-id": getMerryChatLabelFigmaNodeId(type, size),
  });

  if (props.type === "count") {
    const { count, type, ...spanProps } = props;

    return (
      <span {...spanProps} {...getCommonProps(type)}>
        <span
          className="merry-chat-label__count"
          data-figma-text-node-id={getMerryChatLabelTextFigmaNodeId(size)}
        >
          {count}
        </span>
      </span>
    );
  }

  const { type: statusType, ...spanProps } = props;
  const isDot = statusType === "dot";

  return (
    <span {...spanProps} {...getCommonProps(statusType)}>
      <span
        aria-hidden="true"
        className={cn(
          "merry-chat-label__icon",
          isDot && "merry-chat-label__icon--dot",
        )}
      >
        <img alt="" src={statusIcons[statusType]} />
      </span>
    </span>
  );
}
